import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/app/Navbar";
import Footer from "@/components/app/Footer";
import { api, fmtINR } from "@/lib/api";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { BookOpen, Loader2 } from "lucide-react";

export default function MyBookings() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/bookings/me").then(({ data }) => setItems(data)).finally(() => setFetching(false));
  }, [user]);

  if (loading) {
    return <div className="min-h-screen"><Navbar />
      <div className="max-w-7xl mx-auto px-6 py-24 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
    </div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen"><Navbar />
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-24 pb-32 text-center">
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">Sign in to view your bookings</h1>
          <button onClick={startGoogleLogin} className="btn-pill btn-primary mt-8" data-testid="signin-google-btn">Continue with Google</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 pt-12 pb-24">
        <div className="overline">Your reservations</div>
        <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl tracking-tight mb-10">My bookings</h1>

        {fetching ? (
          <div className="text-sm" style={{ color: "var(--text-2)" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div className="surface-card p-10 text-center" data-testid="no-bookings">
            <BookOpen className="w-6 h-6 mx-auto" style={{ color: "var(--forest)" }} />
            <p className="font-serif-display text-2xl mt-3">No bookings yet.</p>
            <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>Find a stay and your bookings will appear here.</p>
            <Link to="/listings" className="btn-pill btn-primary inline-flex mt-6">Browse stays</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((b) => (
              <div key={b.id} className="surface-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`my-booking-${b.id}`}>
                <div>
                  <div className="font-serif-display text-2xl">{b.listing_title}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>
                    {b.check_in} → {b.check_out} · {b.nights} nights · {b.stay_tier} pricing
                  </div>
                  {b.addons?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {b.addons.map((a, i) => (
                        <span key={i} className="text-xs px-2.5 py-1 rounded-full" style={{ background: "rgba(198,125,99,0.12)", color: "var(--terracotta-hover)" }}>
                          {a.key.replace("_", " ")} × {a.qty}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="overline">Total</div>
                  <div className="font-serif-display text-2xl text-[color:var(--forest)]">{fmtINR(b.total_amount)}</div>
                  <span className="text-xs px-3 py-1 mt-2 inline-block rounded-full" style={{ background: "var(--surface-alt)" }}>{b.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
